/**
 * AmbientRecordingPanel - Continuous ambient audio recording with live transcript.
 *
 * Design: DENiZEN — organic, warm, nature-inspired
 * - Rounded corners, soft shadows, green palette
 * - Responsive: full-width mobile, constrained card on desktop
 * - WCAG 2.1 AA compliant (48px touch targets, aria labels)
 *
 * States: idle → consent → recording → processing → results
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useAmbientRecording } from '@/hooks/useAmbientRecording'
import { useAudioLevel } from '@/hooks/useAudioLevel'
import { PebbleVisualiser } from './PebbleVisualiser'
import type { AmbientChunkResponse, AmbientStopResponse, AmbientExtractionResponse } from '@/services/api'

export interface AmbientRecordingPanelProps {
  sessionId: string
  sendChunk: (sessionId: string, audioData: Blob, chunkIndex: number) => Promise<AmbientChunkResponse>
  stopAmbient: (sessionId: string) => Promise<AmbientStopResponse>
  extractAmbient: (sessionId: string) => Promise<AmbientExtractionResponse>
  onExtractionComplete?: (result: AmbientExtractionResponse) => void
  onFinish?: () => void
}

type PanelState = 'idle' | 'consent' | 'starting' | 'recording' | 'processing' | 'results'

/** Format seconds as MM:SS */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

/** Stop SVG icon */
function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  )
}

/** Topic colour mapping */
const TOPIC_COLOURS: Record<string, string> = {
  current_values: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  current_strengths: 'bg-green-100 text-green-800 border-green-200',
  current_challenges: 'bg-amber-100 text-amber-800 border-amber-200',
  future_hopes: 'bg-sky-100 text-sky-800 border-sky-200',
  technology_role: 'bg-violet-100 text-violet-800 border-violet-200',
  technology_concerns: 'bg-orange-100 text-orange-800 border-orange-200',
  sustainability: 'bg-teal-100 text-teal-800 border-teal-200',
}

/** Human-readable topic labels */
const TOPIC_LABELS: Record<string, string> = {
  current_values: 'Values',
  current_strengths: 'Strengths',
  current_challenges: 'Challenges',
  future_hopes: 'Hopes',
  technology_role: 'Technology',
  technology_concerns: 'Tech Concerns',
  sustainability: 'Sustainability',
}

export function AmbientRecordingPanel({
  sessionId,
  sendChunk,
  stopAmbient,
  extractAmbient,
  onExtractionComplete,
  onFinish,
}: AmbientRecordingPanelProps) {
  const [panelState, setPanelState] = useState<PanelState>('idle')
  const [consentGiven, setConsentGiven] = useState(false)
  const transcriptEndRef = useRef<HTMLDivElement>(null)

  const ambient = useAmbientRecording({
    sessionId,
    sendChunk,
    stopAmbient,
    extractAmbient,
  })

  const audioLevel = useAudioLevel()

  // Auto-scroll transcript as new chunks arrive
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [ambient.chunks])

  // Sync panel state with recording state
  useEffect(() => {
    if (ambient.isRecording && panelState !== 'recording') {
      setPanelState('recording')
    }
    if (ambient.isExtracting && panelState !== 'processing') {
      setPanelState('processing')
    }
    if (ambient.extraction && panelState !== 'results') {
      setPanelState('results')
    }
    // If startRecording failed (mic denied / unsupported), drop back to idle so
    // the participant sees the error banner and can retry instead of being stuck
    // on the 'starting' spinner forever.
    if (panelState === 'starting' && !ambient.isRecording && ambient.error) {
      setPanelState('idle')
    }
  }, [ambient.isRecording, ambient.isExtracting, ambient.extraction, ambient.error, panelState])

  const handleStartClick = useCallback(() => {
    setPanelState('consent')
  }, [])

  const handleConsentConfirm = useCallback(() => {
    setConsentGiven(true)
    // flushSync forces React to commit the 'starting' screen to the DOM before
    // getUserMedia fires the browser permission prompt. Without it, React batches
    // the state update with the async work and the browser repaints only after the
    // user responds to the OS prompt — leaving the consent screen frozen.
    flushSync(() => setPanelState('starting'))
    void ambient.startRecording()
      .then(() => {
        // audioLevel.start currently catches all internal errors and surfaces
        // them via its `error` state — this `.catch` is purely defensive against
        // a future regression that lets a throw escape the hook. Audio level is
        // decorative; its failure must not block recording.
        audioLevel.start().catch((err: unknown) => {
          console.warn('[AmbientRecordingPanel] audioLevel.start failed', err)
        })
      })
      .catch((err: unknown) => {
        // startRecording catches its own DOMException paths and surfaces them via
        // ambient.error, but an un-caught throw (programmer error, ref state bug)
        // would otherwise leave the UI stranded on the 'starting' spinner with no
        // banner. Reset to idle so the participant can retry. console.error so
        // App Insights / Sentry captures the unexpected path.
        console.error('[AmbientRecordingPanel] startRecording threw', err)
        setPanelState('idle')
      })
  }, [ambient, audioLevel])

  const handleStop = useCallback(async () => {
    void audioLevel.stop()
    setPanelState('processing')

    const stopResult = await ambient.stopRecording()
    if (!stopResult) {
      // Backend stop failed — local recording is already stopped, so return to
      // idle rather than showing a stale recording UI. Error banner is still visible.
      setPanelState('idle')
      return
    }

    const result = await ambient.extractThemes()
    if (result) {
      setPanelState('results')
      onExtractionComplete?.(result)
    } else {
      // Extraction failed. ambient.error surfaces what happened. There is no
      // admin re-extract control yet, so the user must restart recording.
      setPanelState('idle')
    }
  }, [ambient, audioLevel, onExtractionComplete])

  const handleReset = useCallback(() => {
    ambient.reset()
    void audioLevel.stop()
    setConsentGiven(false)
    setPanelState('idle')
  }, [ambient, audioLevel])

  return (
    <div className="w-full max-w-2xl mx-auto animate-slide-up">
      {/* Idle state */}
      {panelState === 'idle' && (
        <div className="text-center space-y-4 p-6">
          <PebbleVisualiser
            bars={[0, 0, 0, 0, 0, 0, 0]}
            isActive={false}
            className="mx-auto"
          />
          <h3 className="text-xl font-semibold text-dark-green">
            Ambient Recording
          </h3>
          <p className="text-dark-green/70 max-w-md mx-auto">
            Record a group conversation and automatically extract community themes,
            values, and hopes.
          </p>
          <button
            onClick={handleStartClick}
            className="btn-denizen-primary px-8 py-3 text-lg"
            aria-label="Start ambient recording"
          >
            Start Recording
          </button>
        </div>
      )}

      {/* Consent state */}
      {panelState === 'consent' && (
        <div className="space-y-4 p-6">
          <h3 className="text-lg font-semibold text-dark-green">
            Recording Consent
          </h3>
          <div className="border border-dark-green/10 rounded-2xl bg-white/40 p-4 space-y-3">
            <p className="text-dark-green/80 text-sm">
              This session will record ambient audio from your group conversation.
              The audio will be:
            </p>
            <ul className="text-dark-green/70 text-sm space-y-1 list-disc pl-5">
              <li>Transcribed to text automatically</li>
              <li>Analysed to extract community themes</li>
              <li>Audio is <strong>not stored</strong> — only the text transcript is kept</li>
              <li>Personal names and identifying details are excluded</li>
            </ul>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="checkbox-denizen mt-0.5 shrink-0"
              aria-label="I consent to ambient recording"
            />
            <span className="text-dark-green/80 text-sm">
              All participants consent to this conversation being recorded and transcribed.
            </span>
          </label>
          <div className="flex gap-3">
            <button
              onClick={handleConsentConfirm}
              disabled={!consentGiven}
              className="btn-denizen-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Confirm consent and start recording"
            >
              Begin Recording
            </button>
            <button
              onClick={() => setPanelState('idle')}
              className="btn-denizen-secondary px-6 py-2"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Starting state — bridges consent click to mic-active so the participant
          isn't left wondering whether their tap registered. */}
      {panelState === 'starting' && (
        <div
          className="text-center space-y-4 p-6"
          role="status"
          aria-live="polite"
        >
          <PebbleVisualiser
            bars={[1, 2, 3, 4, 3, 2, 1]}
            isActive
            className="mx-auto animate-pulse"
          />
          <h3 className="text-xl font-semibold text-dark-green">
            Starting recording…
          </h3>
          <p className="text-dark-green/70 text-sm max-w-md mx-auto">
            Requesting microphone access. Allow the prompt if your browser asks.
          </p>
        </div>
      )}

      {/* Recording state */}
      {panelState === 'recording' && (
        <div className="space-y-4 p-6">
          {/* Header with status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
              <span className="text-dark-green font-medium">Recording</span>
            </div>
            <span className="text-dark-green/60 font-mono text-lg" aria-label={`Duration: ${formatDuration(ambient.totalDuration)}`}>
              {formatDuration(ambient.totalDuration)}
            </span>
          </div>

          {/* Audio level visualisation — pebble ring */}
          <PebbleVisualiser
            bars={audioLevel.bars}
            isActive={audioLevel.isActive}
            className="mx-auto"
          />

          {/* Live transcript */}
          <div className="border border-dark-green/10 rounded-2xl bg-white/40 p-4 max-h-60 overflow-y-auto">
            {ambient.chunks.length === 0 ? (
              <p className="text-dark-green/40 text-sm italic">
                Listening... transcript will appear here.
              </p>
            ) : (
              <div className="space-y-2">
                {ambient.chunks.map((chunk) => (
                  <p key={chunk.chunkIndex} className="text-dark-green/80 text-sm">
                    {chunk.text}
                  </p>
                ))}
                <div ref={transcriptEndRef} />
              </div>
            )}
          </div>

          {/* Word count */}
          <p className="text-dark-green/50 text-xs text-center">
            {ambient.cumulativeWordCount} words transcribed
          </p>

          {/* Stop button */}
          <div className="text-center">
            <button
              onClick={() => void handleStop()}
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-full text-lg font-medium transition-colors min-w-[48px] min-h-[48px]"
              aria-label="Stop recording and extract themes"
            >
              <StopIcon className="w-5 h-5" />
              Stop & Extract
            </button>
          </div>
        </div>
      )}

      {/* Processing state */}
      {panelState === 'processing' && (
        <div className="text-center space-y-4 p-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-mid-green/10 flex items-center justify-center animate-pulse">
            <svg className="w-8 h-8 text-mid-green animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-green">
            Extracting Themes
          </h3>
          <p className="text-dark-green/60">
            Analysing {ambient.cumulativeWordCount} words from your conversation...
          </p>
        </div>
      )}

      {/* Results state */}
      {panelState === 'results' && ambient.extraction && (
        <div className="space-y-5 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-dark-green">
              Extracted Themes
            </h3>
            <span className="text-xs text-dark-green/50">
              Confidence: {Math.round(ambient.extraction.confidence * 100)}%
            </span>
          </div>

          {ambient.extraction.persisted === false && (
            <div
              className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm"
              role="status"
            >
              Themes shown below were extracted, but couldn't be saved to the
              database. They will not be available later — copy or screenshot
              anything you need before leaving this screen.
            </div>
          )}

          {/* Summary */}
          <div className="border border-dark-green/10 rounded-2xl bg-white/40 p-4">
            <h4 className="text-sm font-medium text-dark-green/70 mb-2">Summary</h4>
            <p className="text-dark-green/80 text-sm leading-relaxed">
              {ambient.extraction.summary}
            </p>
          </div>

          {/* Theme cards */}
          <div className="space-y-3">
            {ambient.extraction.themes.map((theme) => (
              <div
                key={theme.topic}
                className="border border-dark-green/10 rounded-2xl bg-white/40 p-4"
              >
                <span
                  className={`inline-block px-2 py-0.5 rounded-[6px] text-xs font-medium border mb-2 ${TOPIC_COLOURS[theme.topic] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                >
                  {TOPIC_LABELS[theme.topic] || theme.topic}
                </span>
                <ul className="space-y-1">
                  {theme.points.map((point, i) => (
                    <li key={i} className="text-dark-green/70 text-sm flex gap-2">
                      <span className="text-mid-green mt-0.5" aria-hidden="true">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Key quotes */}
          {ambient.extraction.key_quotes.length > 0 && (
            <div className="border border-dark-green/10 rounded-2xl bg-white/40 p-4">
              <h4 className="text-sm font-medium text-dark-green/70 mb-2">Key Quotes</h4>
              <div className="space-y-2">
                {ambient.extraction.key_quotes.map((quote, i) => (
                  <blockquote
                    key={i}
                    className="text-dark-green/70 text-sm italic border-l-2 border-mid-green/30 pl-3"
                  >
                    &ldquo;{quote}&rdquo;
                  </blockquote>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex gap-4 text-xs text-dark-green/50 justify-center">
            <span>{ambient.extraction.total_words} words</span>
            <span>~{ambient.extraction.participant_count_estimate} speakers</span>
            <span>{formatDuration(ambient.totalDuration)} duration</span>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3">
            <button
              onClick={handleReset}
              className="btn-denizen-secondary px-6 py-2"
              aria-label="Start a new recording"
            >
              New Recording
            </button>
            {onFinish && (
              <button
                onClick={onFinish}
                className="btn-denizen-primary px-6 py-2"
                aria-label="Finish session"
              >
                Finish Session
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {ambient.error && (
        <div
          className="mx-6 mt-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
          role="alert"
        >
          {ambient.error}
        </div>
      )}

      {/* Audio level error — non-blocking. Recording continues without the
          waveform visual; surface so the participant understands why pebbles
          are flat. */}
      {audioLevel.error && (
        <div
          className="mx-6 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm"
          role="status"
        >
          Audio level visualisation unavailable: {audioLevel.error}
        </div>
      )}
    </div>
  )
}
